// UIFIX2 (SF-10, design/style-guide.md section 10): the one failure state. A plain sentence, a "!" with words so the
// state is never carried by colour alone, and a styled Try again. It never shows raw server text, a key name or the
// words "unexpected server error": the caller passes the sentence a person reads. Text is set with textContent only.

import { showConsoleToast } from "./consoleToast.ts";

export interface FailureStateOptions {
  /** The plain sentence, for example "Possible duplicates could not load." */
  readonly sentence: string;
  /** Runs when Try again is tapped. Omit it for a failure that retrying cannot fix. */
  readonly onRetry?: (() => void) | undefined;
  readonly testid?: string | undefined;
  /** ERRTRACK1: the short reference code for this failure, such as K7M2-94QX. Shown as a second line. */
  readonly referenceCode?: string | undefined;
  /** ERRTRACK1: a Copy code button next to the code. Consoles only; the floor shows the code and nothing to copy. */
  readonly allowCopy?: boolean | undefined;
  /** SUPPORT1: a Report this button next to the code (consoles only). It opens the help form with the code filled in. */
  readonly onReport?: (() => void) | undefined;
  /** ERRTRACK1: the second line when a code is shown. Defaults to the console wording. */
  readonly codeHint?: string | undefined;
}

const DEFAULT_CODE_HINT = "Give this code to support if it keeps happening.";

/** Builds the failure state element. */
export function createFailureState(options: FailureStateOptions): HTMLElement {
  const root = document.createElement("div");
  root.className = "failure-state";
  root.setAttribute("role", "alert");
  if (options.testid !== undefined) {
    root.dataset["testid"] = options.testid;
  }
  const symbol = document.createElement("span");
  symbol.className = "failure-state-symbol";
  symbol.setAttribute("aria-hidden", "true");
  symbol.textContent = "!";
  const text = document.createElement("span");
  text.className = "failure-state-text";
  if (options.referenceCode === undefined) {
    text.textContent = options.sentence;
  } else {
    // The sentence is bold on its own line with the code beneath it, as drawn in the ERRTRACK1 mockups.
    const sentence = document.createElement("b");
    sentence.textContent = options.sentence;
    const detail = document.createElement("span");
    detail.className = "failure-state-detail";
    const code = document.createElement("span");
    code.className = "failure-state-code";
    code.dataset["testid"] = "failure-state-code";
    code.textContent = options.referenceCode;
    detail.append("Code ", code, `. ${options.codeHint ?? DEFAULT_CODE_HINT}`);
    text.append(sentence, detail);
  }
  root.append(symbol, text);
  const referenceCode = options.referenceCode;
  if (referenceCode !== undefined && options.allowCopy === true) {
    const copy = document.createElement("button");
    copy.type = "button";
    copy.className = "btn-secondary btn-compact";
    copy.dataset["testid"] = "failure-state-copy";
    copy.textContent = "Copy code";
    copy.addEventListener("click", () => {
      const done = (message: string): void => showConsoleToast(message);
      if (typeof navigator.clipboard?.writeText !== "function") {
        done("Copy failed");
        return;
      }
      navigator.clipboard.writeText(referenceCode).then(
        () => done("Code copied"),
        () => done("Copy failed"),
      );
    });
    root.append(copy);
  }
  const report = options.onReport;
  if (referenceCode !== undefined && report !== undefined) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "btn-secondary btn-compact";
    button.dataset["testid"] = "failure-state-report";
    button.textContent = "Report this";
    button.addEventListener("click", () => report());
    root.append(button);
  }
  const retry = options.onRetry;
  if (retry !== undefined) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "btn-secondary btn-compact failure-state-retry";
    button.textContent = "Try again";
    button.addEventListener("click", () => retry());
    root.append(button);
  }
  return root;
}
